import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { format, differenceInDays } from "date-fns";
import leaveApiService from "../services/leave-api";
import holidayApiService from "../services/holiday-api";
import { LeaveBalance, LeaveType, CreateLeaveRequest } from "../types/leave";
import ConfirmActionDialog from "../pages/Leave/components/ConfirmActionDialog";

interface NewRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NewRequestDialog: React.FC<NewRequestDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [selectedType, setSelectedType] = useState<LeaveType | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reason, setReason] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<
    "MORNING" | "AFTERNOON" | ""
  >("");

  // Confirmation step shown before the request is actually submitted
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Data
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [nonWorkdayDates, setNonWorkdayDates] = useState<Set<string>>(
    new Set(),
  );
  const [userOfficeLocation, setUserOfficeLocation] = useState<string>("");

  // Latest date an employee may file for: Dec 31 of the next calendar year.
  const maxFilingDate = useMemo(
    () => new Date(new Date().getFullYear() + 1, 11, 31),
    [],
  );

  // Whether the chosen start date falls in the next calendar year.
  const isNextYearRequest = useMemo(() => {
    if (!startDate) return false;
    return startDate.getFullYear() > new Date().getFullYear();
  }, [startDate]);

  // Helper function to format date as local date string (YYYY-MM-DD)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Count workdays in an inclusive [start, end] range, excluding non-workdays
  // (weekends + holidays, minus workday overrides) using the loaded set.
  const countWorkdays = (from: Date, to: Date): number => {
    const totalCalendarDays = differenceInDays(to, from) + 1;
    let workdayCount = 0;
    for (let i = 0; i < totalCalendarDays; i++) {
      const currentDate = new Date(from);
      currentDate.setDate(currentDate.getDate() + i);
      if (!nonWorkdayDates.has(formatLocalDate(currentDate))) {
        workdayCount++;
      }
    }
    return workdayCount;
  };

  // Whether the selected range crosses a calendar-year boundary. Only full-day
  // requests can span multiple days, so half-day requests are never cross-year.
  const isCrossYear = useMemo(() => {
    if (!startDate || !endDate || isHalfDay) return false;
    return startDate.getFullYear() !== endDate.getFullYear();
  }, [startDate, endDate, isHalfDay]);

  // When a range crosses New Year, it is split into two requests:
  //   segment 1: startDate .. Dec 31 of the start year
  //   segment 2: Jan 1 of the end year .. endDate
  const crossYearSegments = useMemo(() => {
    if (!startDate || !endDate || !isCrossYear) return null;
    const startYear = startDate.getFullYear();
    const decEnd = new Date(startYear, 11, 31); // Dec 31 of start year
    const janStart = new Date(startYear + 1, 0, 1); // Jan 1 of next year
    return {
      first: { start: startDate, end: decEnd },
      second: { start: janStart, end: endDate },
    };
  }, [startDate, endDate, isCrossYear]);

  // Calculated values
  const daysRequested = useMemo(() => {
    if (!startDate || !endDate) return 0;
    if (isHalfDay) return 0.5;
    return countWorkdays(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, isHalfDay, nonWorkdayDates]);

  const selectedBalance = useMemo(
    () =>
      balances.find((b) => selectedType && b.leaveTypeId === selectedType.id),
    [balances, selectedType],
  );

  // Memoize filtered leave types to prevent expensive re-renders
  const filteredLeaveTypes = useMemo(
    () =>
      leaveTypes
        .filter((type) => {
          const balance = balances.find((b) => b.leaveTypeId === type.id);
          // Always show Unpaid Leave even if 0 days, filter out others with 0 days
          return (
            !balance || balance.availableDays > 0 || type.code === "UNPAID"
          );
        })
        .map((type) => {
          const balance = balances.find((b) => b.leaveTypeId === type.id);
          return { type, balance };
        }),
    [leaveTypes, balances],
  );

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesResponse, balancesResponse, profileResponse] =
        await Promise.all([
          leaveApiService.getActiveLeaveTypes(),
          leaveApiService.getMyBalances(),
          leaveApiService.getMyProfile(),
        ]);

      setLeaveTypes(typesResponse);
      setBalances(balancesResponse);

      // Get user's office location
      const officeLocation = profileResponse.officeLocation || "";
      setUserOfficeLocation(officeLocation);

      // Load non-workdays across a wide window so both backdated filing and future
      // requests have accurate weekend/holiday data for the date picker and the
      // workday count in the summary.
      const today = new Date();

      // Start from Jan 1 of the previous calendar year (covers backdated filing)
      const startDate = new Date(today.getFullYear() - 1, 0, 1);

      // End at Dec 31 of the next calendar year, so late next-year dates (which
      // employees are allowed to file for) have accurate weekend/holiday data.
      const endDate = new Date(today.getFullYear() + 1, 11, 31);

      // Format dates as local dates (YYYY-MM-DD) without timezone conversion
      const startDateStr = formatLocalDate(startDate);
      const endDateStr = formatLocalDate(endDate);

      try {
        const nonWorkdays = await holidayApiService.getNonWorkdaysByDateRange(
          startDateStr,
          endDateStr,
          officeLocation,
        );
        setNonWorkdayDates(new Set(nonWorkdays));
      } catch (err) {
        console.error(t("error.errorLoadingNonWorkdays"), err);
        // Continue without non-workday filtering if it fails
      }
    } catch {
      setError(t("leave.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const shouldDisableDate = (date: Date): boolean => {
    // Format date as local date string (YYYY-MM-DD) without timezone conversion
    const dateStr = formatLocalDate(date);
    // Disable if it's a non-workday (holiday, weekend, or workday override)
    return nonWorkdayDates.has(dateStr);
  };

  const shouldDisableStartDate = (date: Date): boolean => {
    // Backdated filing is allowed: no minimum-date floor. Employees may select
    // past, current, or future dates. Only non-workdays (holidays/weekends that
    // are not overridden) are disabled since they cannot be leave days.
    const dateStr = formatLocalDate(date);
    return nonWorkdayDates.has(dateStr);
  };

  const shouldDisableEndDate = (date: Date): boolean => {
    // Disable non-workdays
    const dateStr = formatLocalDate(date);
    const isNonWorkday = nonWorkdayDates.has(dateStr);
    if (isNonWorkday) {
      return true;
    }
    // If start date is selected, disable dates before start date
    if (startDate && date < startDate) {
      return true;
    }
    return false;
  };

  // Validate the form, then open the confirmation dialog. The request is only
  // sent to the backend after the user confirms (see handleConfirmedSubmit).
  const handleSubmit = () => {
    if (!selectedType || !startDate || !endDate) {
      setError(t("leave.messages.fillRequired"));
      return;
    }

    // Half-day validation
    if (isHalfDay) {
      if (!halfDayPeriod) {
        setError(t("error.selectHalfDayPeriod"));
        return;
      }
      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        setError(t("error.halfDayMustBeSingleDay"));
        return;
      }
    }

    if (selectedBalance && daysRequested > selectedBalance.availableDays) {
      setError(t("leave.messages.insufficientBalance"));
      return;
    }

    setError(null);
    setConfirmOpen(true);
  };

  // Build a CreateLeaveRequest payload for a given [start, end] segment.
  const buildRequest = (from: Date, to: Date): CreateLeaveRequest => ({
    leaveTypeId: selectedType!.id,
    startDate: format(from, "yyyy-MM-dd"),
    endDate: format(to, "yyyy-MM-dd"),
    reason: reason.trim() || undefined,
    isHalfDay: isHalfDay,
    halfDayPeriod:
      isHalfDay && halfDayPeriod
        ? (halfDayPeriod as "MORNING" | "AFTERNOON")
        : undefined,
  });

  // Actually submit after confirmation. A cross-year range is submitted as two
  // separate requests (December segment, then January segment). Errors are thrown
  // so the ConfirmActionDialog can surface them and keep itself open.
  const handleConfirmedSubmit = async () => {
    if (!selectedType || !startDate || !endDate) return;

    if (isCrossYear && crossYearSegments) {
      // Submit the December segment first, then the January segment. If the
      // second fails, the thrown error is shown; the first has already been
      // created, which the error message makes clear to the user.
      await leaveApiService.createLeaveRequest(
        buildRequest(crossYearSegments.first.start, crossYearSegments.first.end),
      );
      try {
        await leaveApiService.createLeaveRequest(
          buildRequest(
            crossYearSegments.second.start,
            crossYearSegments.second.end,
          ),
        );
      } catch (err: any) {
        throw new Error(
          t("leave.messages.crossYearSecondFailed", {
            defaultValue:
              "The first (December) request was created, but the second (January) request failed: {{reason}}. Please file the January request separately.",
            reason: err?.message || "unknown error",
          }),
        );
      }
    } else {
      await leaveApiService.createLeaveRequest(buildRequest(startDate, endDate));
    }

    onSuccess();
    handleClose();
  };

  // Whether the selected start date is strictly in the past (for the confirm note).
  const isPastStart = (): boolean => {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    return start.getTime() < today.getTime();
  };

  const handleClose = () => {
    setSelectedType(null);
    setStartDate(null);
    setEndDate(null);
    setReason("");
    setIsHalfDay(false);
    setHalfDayPeriod("");
    setError(null);
    setConfirmOpen(false);
    onClose();
  };

  const isFormValid =
    selectedType &&
    startDate &&
    endDate &&
    startDate <= endDate &&
    (!isHalfDay || (isHalfDay && halfDayPeriod));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("leave.newRequest")}</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {isNextYearRequest && startDate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("leave.nextYearFilingNotice", {
                  year: startDate.getFullYear(),
                })}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Leave Type Selection */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label={t("leave.leaveType")}
                  value={selectedType?.id || ""}
                  onChange={(e) => {
                    const type = leaveTypes.find(
                      (t) => t.id === Number(e.target.value),
                    );
                    setSelectedType(type || null);
                  }}
                  required
                >
                  {filteredLeaveTypes.map(({ type, balance }) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{type.name}</span>
                        {balance && (
                          <Chip
                            label={`${balance.availableDays} ${t("leave.days")}`}
                            size="small"
                            color={
                              balance.availableDays > 0 ? "primary" : "default"
                            }
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Date Selection */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("leave.startDate")}
                    value={startDate}
                    maxDate={maxFilingDate}
                    onChange={(newDate) => {
                      setStartDate(newDate);
                      // Auto-populate end date if it's empty
                      if (newDate && !endDate) {
                        setEndDate(newDate);
                      }
                    }}
                    shouldDisableDate={shouldDisableStartDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("leave.endDate")}
                    value={endDate}
                    maxDate={maxFilingDate}
                    onChange={setEndDate}
                    shouldDisableDate={shouldDisableEndDate}
                    defaultCalendarMonth={startDate || undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Half-day options */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isHalfDay}
                      onChange={(e) => {
                        const halfDay = e.target.checked;
                        setIsHalfDay(halfDay);
                        if (halfDay && startDate) {
                          setEndDate(startDate);
                        } else {
                          setHalfDayPeriod("");
                        }
                      }}
                    />
                  }
                  label={t("users.halfDay")}
                />
              </Grid>

              {isHalfDay && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t("users.halfDayPeriod")}</InputLabel>
                    <Select
                      value={halfDayPeriod}
                      onChange={(e) =>
                        setHalfDayPeriod(
                          e.target.value as "MORNING" | "AFTERNOON",
                        )
                      }
                      label={t("users.halfDayPeriod")}
                      required
                    >
                      <MenuItem value="MORNING">{t("users.morning")}</MenuItem>
                      <MenuItem value="AFTERNOON">
                        {t("users.afternoon")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Request Summary */}
              {selectedType && startDate && endDate && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "grey.50",
                      borderRadius: 1,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      {t("leave.requestSummary")}
                    </Typography>
                    <Box>
                      <Typography variant="body2" component="div">
                        {selectedType.name}
                        {" : "}
                        <strong>
                          {format(startDate, "MMM dd, yyyy")} -{" "}
                          {format(endDate, "MMM dd, yyyy")}
                        </strong>
                      </Typography>

                      <Typography
                        variant="body2"
                        color="secondary"
                        component="div"
                      >
                        <strong>
                          {daysRequested}{" "}
                          {daysRequested === 1
                            ? t("leave.day")
                            : t("leave.days")}
                        </strong>
                      </Typography>

                      {isHalfDay && halfDayPeriod && (
                        <Chip
                          label={`${t("leave.halfDay")} - ${halfDayPeriod === "MORNING" ? "AM" : "PM"}`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    {selectedBalance && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {t("leave.availableBalance")}:{" "}
                          {selectedBalance.availableDays}{" "}
                          {selectedBalance.availableDays === 1
                            ? t("leave.day")
                            : t("leave.days")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            selectedBalance.availableDays - daysRequested < 0
                              ? "error"
                              : "success.main"
                          }
                          sx={{ mt: 0.5, fontWeight: 600 }}
                        >
                          {t("leave.balanceAfter")}:{" "}
                          {(
                            selectedBalance.availableDays - daysRequested
                          ).toFixed(1)}{" "}
                          {Math.abs(
                            selectedBalance.availableDays - daysRequested,
                          ) === 1
                            ? t("leave.day")
                            : t("leave.days")}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Reason */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t("leave.reason")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("leave.reasonPlaceholder")}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
        >
          {t("leave.submitRequest")}
        </Button>
      </DialogActions>

      {/* Confirmation step before submitting (highlights dates & total days) */}
      <ConfirmActionDialog
        open={confirmOpen}
        title={
          isCrossYear
            ? t("leave.confirmSplitTitle")
            : t("leave.confirmSubmitTitle")
        }
        message={
          isCrossYear
            ? t("leave.confirmSplitMessage")
            : t("leave.confirmSubmitMessage")
        }
        details={
          selectedType && startDate && endDate ? (
            isCrossYear && crossYearSegments ? (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {selectedType.name}
                </Typography>
                {/* Segment 1: December */}
                <Typography variant="body2" sx={{ mt: 1 }} fontWeight={600}>
                  {t("leave.requestNumber", { number: 1 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(crossYearSegments.first.start, "MMM dd, yyyy")} -{" "}
                  {format(crossYearSegments.first.end, "MMM dd, yyyy")} (
                  {countWorkdays(
                    crossYearSegments.first.start,
                    crossYearSegments.first.end,
                  )}{" "}
                  {t("leave.days")})
                </Typography>
                {/* Segment 2: January */}
                <Typography variant="body2" sx={{ mt: 1 }} fontWeight={600}>
                  {t("leave.requestNumber", { number: 2 })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(crossYearSegments.second.start, "MMM dd, yyyy")} -{" "}
                  {format(crossYearSegments.second.end, "MMM dd, yyyy")} (
                  {countWorkdays(
                    crossYearSegments.second.start,
                    crossYearSegments.second.end,
                  )}{" "}
                  {t("leave.days")})
                </Typography>
                {crossYearSegments.second.end.getFullYear() >
                  new Date().getFullYear() && (
                  <Typography
                    variant="body2"
                    color="info.main"
                    sx={{ mt: 1 }}
                  >
                    {t("leave.nextYearFilingNotice", {
                      year: crossYearSegments.second.end.getFullYear(),
                    })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {selectedType.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(startDate, "MMM dd, yyyy")} -{" "}
                  {format(endDate, "MMM dd, yyyy")} ({daysRequested}{" "}
                  {daysRequested === 1 ? t("leave.day") : t("leave.days")})
                </Typography>
                {isPastStart() && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mt: 1 }}
                  >
                    {t("leave.confirmSubmitPastWarning")}
                  </Typography>
                )}
                {isNextYearRequest && (
                  <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                    {t("leave.nextYearFilingNotice", {
                      year: startDate.getFullYear(),
                    })}
                  </Typography>
                )}
              </>
            )
          ) : undefined
        }
        confirmLabel={
          isCrossYear
            ? t("leave.confirmSplitSubmit")
            : t("leave.submitRequest")
        }
        cancelLabel={t("common.close")}
        confirmColor="primary"
        onConfirm={handleConfirmedSubmit}
        onClose={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default NewRequestDialog;
